import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

// NOTE: localStorage here stores UI preferences only — never tokens, PII or other sensitive data.
const SIZES = [10, 25, 50, 100];

export function usePagedSearch(rows, storageKey, matcher, defaultSize = 25) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(() => Number(localStorage.getItem(storageKey)) || defaultSize);
  const setPageSize = (v) => { setPageSizeRaw(Number(v)); localStorage.setItem(storageKey, String(v)); setPage(1); };
  const setQuery = (v) => { setQ(v); setPage(1); };
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? (rows || []).filter((r) => matcher(r, t)) : (rows || []);
  }, [rows, q, matcher]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = Math.min(page, pages);
  const shown = filtered.slice((cur - 1) * pageSize, cur * pageSize);
  return { q, setQuery, page: cur, pages, pageSize, setPageSize, setPage, filtered, shown };
}

export function SearchBox({ value, onChange, placeholder, testId, className = "w-64" }) {
  return (
    <div className={`relative ${className}`}>
      <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-8" data-testid={testId} />
    </div>
  );
}

export function ServerPager({ page, pages, total, pageSize, sizes, onPage, onSize, noun = "rows", testId }) {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs text-muted-foreground" data-testid={testId}>
      <span data-testid={`${testId}-count`}>{total === 0 ? `No ${noun} match` : `Showing ${start}–${end} of ${total}`}</span>
      <div className="flex items-center gap-2">
        <span>Per page</span>
        <Select value={String(pageSize)} onValueChange={(v) => onSize(Number(v))}>
          <SelectTrigger className="h-7 w-20" data-testid={`${testId}-size`}><SelectValue /></SelectTrigger>
          <SelectContent>{sizes.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testId}-prev`}>‹ Prev</Button>
        <span className="font-mono-accent" data-testid={`${testId}-page`}>{page} / {pages}</span>
        <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testId}-next`}>Next ›</Button>
      </div>
    </div>
  );
}

export function Pager({ p, total, noun = "rows", testId, extra }) {
  const start = total === 0 ? 0 : (p.page - 1) * p.pageSize + 1;
  const end = Math.min(p.page * p.pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs text-muted-foreground" data-testid={testId}>
      <span data-testid={`${testId}-count`}>{total === 0 ? `No ${noun} match` : `Showing ${start}–${end} of ${total}`}{p.q && ` · filtered by “${p.q}”`}{extra}</span>
      <div className="flex items-center gap-2">
        <span>Per page</span>
        <Select value={String(p.pageSize)} onValueChange={p.setPageSize}>
          <SelectTrigger className="h-7 w-20" data-testid={`${testId}-size`}><SelectValue /></SelectTrigger>
          <SelectContent>{SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="ghost" disabled={p.page <= 1} onClick={() => p.setPage(p.page - 1)} data-testid={`${testId}-prev`}>‹ Prev</Button>
        <span className="font-mono-accent" data-testid={`${testId}-page`}>{p.page} / {p.pages}</span>
        <Button size="sm" variant="ghost" disabled={p.page >= p.pages} onClick={() => p.setPage(p.page + 1)} data-testid={`${testId}-next`}>Next ›</Button>
      </div>
    </div>
  );
}
